import { fragment, icon, node } from "~/util/dom";
import flatpickr from "flatpickr/dist/esm";
import { _, CONFIG } from "~/route/globals";
import createSDSIcon from "~/ui/icon";
import PopOver from "~/ui/popover";
import { clearTimeFromDate, getDayDiff } from "~/route4/utils";
import { ICON_ARROW_LEFT_20, ICON_ARROW_RIGHT_20, ICON_CALENDAR_MONTH_20 } from "~/ui/icons";

export interface IPUBTDateTime {
	date: number;
	time: number;
	departure: boolean;
}

enum SideType {
	LEFT = "left",
	RIGHT = "right",
}

const TIME_STEP = 10;

function createArrowContainer(content: HTMLElement, className = "", callback?: (type: SideType) => void) {
	const container = node("div", { className });
	const leftButton = node("button", { type: "button", className: "button-left" });
	const rightButton = node("button", { type: "button", className: "button-right" });

	leftButton.appendChild(createSDSIcon({
		symbol: ICON_ARROW_LEFT_20,
	}));
	rightButton.appendChild(createSDSIcon({
		symbol: ICON_ARROW_RIGHT_20,
	}));

	if (callback) {
		leftButton.addEventListener("click", () => {
			callback(SideType.LEFT);
		});
		rightButton.addEventListener("click", () => {
			callback(SideType.RIGHT);
		});
	}

	container.appendChild(leftButton);
	container.appendChild(content);
	container.appendChild(rightButton);

	return container;
}

export default class ParamsItemPUBTDateTime {
	#buttonDate: HTMLButtonElement;

	#inputDate: HTMLInputElement;

	#timeInput: HTMLInputElement;

	#label: HTMLDivElement;

	#popup: PopOver;

	#toggleButton: HTMLButtonElement;

	#iconClosed: SVGSVGElement | HTMLSpanElement;

	#iconOpen: SVGSVGElement | HTMLSpanElement;

	/** datum pro uchovavani data */
	#date: Date;

	/** datum pro uchovavani casu */
	#time: Date;

	#calendar: ReturnType<flatpickr>;

	#timePicker: ReturnType<flatpickr>;

	#content: HTMLDivElement;

	#buttonNow: HTMLButtonElement;

	#departureInput: HTMLInputElement;

	#arrivalInput: HTMLInputElement;

	#datetime: string;

	#departure: boolean;

	#dateSelect: HTMLDivElement;

	#callback: () => void;

	constructor(callback: () => void) {
		this.#calendar = null;
		this.#callback = callback;
		this.#content = node("div");
		this.#label = node("div", { className: "params-select-label" });
		this.#popup = new PopOver({
			anchor: this.#label,
			content: this.#content,
			className: "params-select-popup datetime",
			width: 298,
			onClose: () => {
				this.#close();
			},
			offset: 8,
		});
		this.#build();

		// nastavime si uvodni stav
		this.state = {
			datetime: "",
			departure: true,
		};
	}

	destroy() {
		this.#close();
		this.#toggleButton.removeEventListener("click", this, false);
		this.#calendar?.destroy();
		this.#calendar = null;
		this.#popup?.destroy();
		this.#popup = null;
		this.#label.removeEventListener("click", this, false);
	}

	get label() {
		return this.#label;
	}

	set state(value: {
		datetime: string;
		departure: boolean;
	}) {
		// pokud je vse stejne jako predtim, tak nic nedelame
		if (this.#datetime === value.datetime && this.#departure === value.departure) {
			return;
		}

		// zmenilo se datum
		if (this.#datetime !== value.datetime) {
			this.#datetime = value.datetime || "";
			this.#time = this.#datetime ? new Date(this.#datetime) : new Date();
			this.#date = this.#datetime ? new Date(this.#datetime) : new Date();
			clearTimeFromDate(this.#date);

			// aktualizujeme DOM
			this.#calendar?.setDate(this.#date.getTime());
			this.#timePicker?.setDate(this.#time.getTime());

			if (!this.#datetime) {
				this.#timeInput.value = "";
				this.#inputDate.value = "";
			}

			this.#buttonDate.querySelector("span").innerText = this.#formatDate();

			if (this.#date.getTime() <= Date.now()) {
				this.#content.querySelector(".dateSelect .button-left")?.setAttribute("disabled", "disabled");
			} else {
				this.#content.querySelector(".dateSelect .button-left")?.removeAttribute("disabled");
			}

			// zobrazeni/schovani tlacitka pro nastaveni casu "ted"
			this.#buttonNow.classList[this.#datetime ? "remove" : "add"]("hide");
		}

		// zmenil se odjezd/prijezd
		if (this.#departure !== value.departure) {
			this.#departure = !!value.departure;
			(this.#departure ? this.#departureInput : this.#arrivalInput).checked = true;
		}

		// neco se zmenilo, tak aktualizujeme label
		this.#label.querySelector("span").innerText = this.#datetime
			? `${this.#departure ? _("route.form.departure2") : _("route.form.arrival")} ${this.#formatDate(true)} ${this.#timeInput.value}`
			: this.#departure ? `${_("route.form.departure2")} ${_("time.now")}` : `${_("route.form.arrival")} ${_("time.now")}`;
	}

	get state(): {
		datetime: string;
		departure: boolean;
		} {
		return {
			datetime: this.#datetime,
			departure: this.#departure,
		};
	}

	handleEvent(event: Event) {
		const target = event.target as Node;

		if (this.#label.contains(target) || this.#label === target) {
			if (this.#popup.isOpen) {
				this.#popup.close();
			} else {
				this.#open();
			}
		} else if (this.#buttonDate.contains(target) || this.#buttonDate === target) {
			this.#calendar.setDate(this.#date);
			this.#dateSelect.replaceWith(this.#calendar.calendarContainer);
		}
	}

	#close() {
		this.#iconOpen.remove();
		this.#toggleButton.insertBefore(this.#iconClosed, this.#toggleButton.firstChild);

		if (this.#calendar.calendarContainer.closest(".params-select-popup")) {
			this.#calendar.calendarContainer.replaceWith(this.#dateSelect);
		}

		// osetreni, pokud uzivatel zadaval cas rucne klavesnici a input neztratil focus
		const datetime = this.#datetime ? new Date(this.#datetime) : new Date();
		const time = this.#timePicker.input.value.split(":");

		if (time.length === 2 && !isNaN(Number(time[0])) && !isNaN(Number(time[1]))) {
			datetime.setHours(time[0]);
			datetime.setMinutes(time[1]);
		}

		this.state = {
			datetime: Mapy.Util.dateToISO(datetime),
			departure: this.#departure,
		};

		this.#label.classList.remove("open");
		this.#callback();
	}

	#open() {
		this.#iconClosed.remove();
		this.#toggleButton.insertBefore(this.#iconOpen, this.#toggleButton.firstChild);
		this.#popup.open();
		this.#label.classList.add("open");
	}

	#build() {
		this.#buttonDate = node("button", { type: "button" });
		this.#inputDate = node("input", { type: "hidden" });
		this.#timeInput = node("input", { type: "hidden" });
		this.#toggleButton = node("button");
		this.#iconClosed = icon("forward");
		this.#iconOpen = icon("dropdown");
		this.#buttonNow = node("button", { type: "button", className: "btn-now" }, `${_("route.form.departure2")} ${_("time.rightNow")}`);
		this.#departureInput = node("input", { type: "radio", id: "pubtDepartureRadio", name: "departureArrival" });
		this.#arrivalInput = node("input", { type: "radio", id: "pubtArrivalRadio", name: "departureArrival" });
		this.#toggleButton.appendChild(this.#iconClosed);
		this.#label.appendChild(this.#toggleButton);
		this.#buildCalendar();

		const buttonsContainer = node("div", { className: "buttons" });
		const departure = node("label", {}, _("route.form.departure2"));
		const arrival = node("label", {}, _("route.form.arrival"));
		const bottomContainer = node("div", { className: "bottom" });
		const doneBtn = node("mapy-router-button", {}, _("done"));
		const dateContainer = node("div");
		const iconCalendar = createSDSIcon({
			symbol: ICON_CALENDAR_MONTH_20,
		});

		this.#label.addEventListener("click", this, false);
		iconCalendar.classList.add("calendar");

		departure.setAttribute("for", "pubtDepartureRadio");
		arrival.setAttribute("for", "pubtArrivalRadio");

		this.#departureInput.addEventListener("change", () => {
			this.state = {
				datetime: this.#datetime,
				departure: true,
			};
		});
		this.#arrivalInput.addEventListener("change", () => {
			this.state = {
				datetime: this.#datetime,
				departure: false,
			};
		});

		this.#label.appendChild(node("span", {}));

		const frag = fragment();

		buttonsContainer.appendChild(this.#departureInput);
		buttonsContainer.appendChild(departure);
		buttonsContainer.appendChild(this.#arrivalInput);
		buttonsContainer.appendChild(arrival);

		this.#buttonNow.addEventListener("click", () => {
			this.state = {
				datetime: "",
				departure: this.#departure,
			};
		});

		doneBtn.addEventListener("click", () => {
			this.#popup.close();
		});

		bottomContainer.appendChild(doneBtn);
		bottomContainer.appendChild(this.#buttonNow);

		this.#buttonDate.appendChild(iconCalendar);
		this.#buttonDate.appendChild(node("span", { className: "capitalize" }));

		dateContainer.appendChild(this.#buttonDate);

		this.#buttonDate.addEventListener("click", this, false);

		this.#dateSelect = createArrowContainer(dateContainer, "dateSelect", type => {
			const dateOut = this.#datetime ? new Date(this.#datetime) : new Date();

			dateOut.setDate(dateOut.getDate() + (type === SideType.LEFT ? -1 : 1));
			this.state = {
				datetime: Mapy.Util.dateToISO(dateOut),
				departure: this.#departure,
			};
		});

		frag.appendChild(buttonsContainer);
		frag.appendChild(this.#dateSelect);
		frag.appendChild(createArrowContainer(this.#timeInput, "timeSelect", type => {
			const dateOut = this.#datetime ? new Date(this.#datetime) : new Date();

			this.state = {
				datetime: Mapy.Util.dateToISO(new Date(dateOut.getTime() + (type === SideType.LEFT ? -(TIME_STEP * 60 * 1000) : TIME_STEP * 60 * 1000))),
				departure: this.#departure,
			};
		}));
		frag.appendChild(bottomContainer);

		this.#content.appendChild(frag);
	}

	async #buildCalendar() {
		let LANG = null;

		if (CONFIG.defaultLang === "en") {
			LANG = await import(/* webpackChunkName: "flatpickrLang" */ `flatpickr/dist/esm/l10n/default`);
		} else {
			LANG = await import(/* webpackChunkName: "flatpickrLang" */ `flatpickr/dist/esm/l10n/${CONFIG.defaultLang}`);
		}

		this.#calendar = flatpickr(this.#inputDate, {
			enableTime: false,
			dateFormat: "d. m. Y",
			disableMobile: true,
			minDate: new Date(this.#date.getTime()),
			monthSelectorType: "static",
			...LANG? { locale: LANG.default[CONFIG.defaultLang] } : {},
			onChange: (selectedDates: Array<Date>) => {
				const dateOut = selectedDates[0];
				const datetime = this.#datetime ? new Date(this.#datetime) : new Date();

				// doplnime cas z naseho datetime
				dateOut.setHours(datetime.getHours());
				dateOut.setMinutes(datetime.getMinutes());
				dateOut.setSeconds(datetime.getSeconds());
				dateOut.setMilliseconds(datetime.getMilliseconds());

				this.state = {
					datetime: Mapy.Util.dateToISO(dateOut),
					departure: this.#departure,
				};
			},
			inline: true,
			clickOpens: false,
			static: true,
			nextArrow: createSDSIcon({
				symbol: ICON_ARROW_RIGHT_20,
			}).outerHTML,
			prevArrow: createSDSIcon({
				symbol: ICON_ARROW_LEFT_20,
			}).outerHTML,
		});


		this.#calendar.calendarContainer.classList.add("datePickr");

		this.#timePicker = flatpickr(this.#timeInput, {
			enableTime: true,
			noCalendar: true,
			defaultHour: this.#time.getHours(),
			defaultMinute: this.#time.getMinutes(),
			//minDate: new Date(this.#time.getTime()),
			disableMobile: true,
			...LANG? { locale: LANG.default[CONFIG.defaultLang] } : {},
			onChange: (selectedDates: Array<Date>) => {
				const dateOut = selectedDates[0];
				const datetime = this.#datetime ? new Date(this.#datetime) : new Date();

				// doplnime datum z naseho datetime
				dateOut.setFullYear(datetime.getFullYear());
				dateOut.setMonth(datetime.getMonth());
				dateOut.setDate(datetime.getDate());

				this.state = {
					datetime: Mapy.Util.dateToISO(dateOut),
					departure: this.#departure,
				};
			},
			inline: true,
			clickOpens: false,
			static: true,
			minuteIncrement: TIME_STEP,
			// eslint-disable-next-line camelcase
			time_24hr: true,
		});
	}

	#formatDate(withoutDay = false) {
		const date = this.#datetime ? new Date(this.#datetime) : new Date();
		const diff = getDayDiff(date.getTime());

		if (diff <= 1 && diff > -2) {
			return new Intl.RelativeTimeFormat(CONFIG.defaultLang || "cs", { numeric: "auto" }).format(diff, "day");
		}

		return new Intl.DateTimeFormat(CONFIG.defaultLang || "cs", {
			...withoutDay ? {} : { weekday: "short" },
			day: "numeric",
			month: "numeric",
			year: "numeric",
		}).format(date);
	}
}
